import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Typography from "../Typography";
import DropdownButton from "../DropdownButton";
import Switch from "../Switch";
import { theme } from "../../theme";
import { TICKET_FREE } from "../../constants/eventTypes";

const StyledTicketCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.xs};
  justify-content: space-between;
`;

const TicketInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.xs} 12px 12px;
  }
`;

const TicketHeading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-grow: 1;
  gap: ${({ theme }) => theme.sizing.xxs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 0px;
  }
`;

const TicketActions = styled.div`
  align-items: center;
  display: flex;
`;

const TicketApproval = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xxs};
  width: 100%;
  padding: ${({ theme }) => theme.sizing.sm};
  padding-top: 0px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0px 12px 12px 12px;
  }
`;

const TicketStatus = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xxs};
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs} 12px;
    padding-left: 12px;
  }
`;

const TicketLabelStatus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.successMain};
`;

const TicketCard = ({
	ticket,
	openEditModal,
	handleDeleteTicket,
	onToggleApproval,
	isEventCancelled,
	isEventPast,
}) => {
	const { t } = useTranslation("manageEventRegistration");

	const isDisabled = isEventCancelled || isEventPast;

	return (
		<StyledTicketCard>
			<TicketInfo>
				<TicketHeading>
					<Typography
						$variant="title-5-semibold"
						$variantTablet="title-5-semibold"
						$variantMobile="body-1-semibold"
					>
						{ticket.name}
					</Typography>
					<Typography
						$variant="title-5-semibold"
						$variantTablet="title-5-semibold"
						$variantMobile="body-1-semibold"
						color={theme.colors.defaultWeak}
					>
						{ticket.type === TICKET_FREE ? t("free") : `${ticket.price}€`}
					</Typography>
				</TicketHeading>
				<TicketActions onClick={(e) => e.stopPropagation()}>
					<DropdownButton
						buttonVariant="outline"
						disabled={isDisabled}
						options={[
							{ label: t("edit"), onClick: () => openEditModal(ticket) },
							{
								label: t("delete"),
								onClick: () => handleDeleteTicket(ticket._id),
							},
						]}
					/>
				</TicketActions>
			</TicketInfo>
			{ticket.type === TICKET_FREE && (
				<TicketApproval>
					<Typography
						$variant="body-2-medium"
						color={theme.colors.defaultStrong}
					>
						{t("approvalRequired")}
					</Typography>
					<Switch
						value={ticket.approvalRequired}
						ariaLabel={t("approvalRequired")}
						disabled={isDisabled}
						onChange={(checked) =>
							onToggleApproval && onToggleApproval(ticket._id, checked)
						}
					/>
				</TicketApproval>
			)}
			<TicketStatus>
				<TicketLabelStatus>
					<StatusDot
						style={{
							backgroundColor:
								ticket.availableSeats > 0
									? theme.colors.successMain
									: theme.colors.errorMain,
						}}
					/>
					<Typography
						$variant="body-2-semibold"
						color={
							ticket.availableSeats > 0
								? theme.colors.successMain
								: theme.colors.errorMain
						}
					>
						{ticket.availableSeats > 0 ? t("available") : t("soldOut")}
					</Typography>
					<Typography $variant="body-2-regular">
						{t("seats", { count: ticket.availableSeats })}
					</Typography>
				</TicketLabelStatus>
				{/* <Typography $variant="body-2-regular">{t('availableSeats', { available: ticket.availableSeats, total: ticket.capacity })}</Typography> */}
				<Typography $variant="body-2-regular">
					Total: {ticket.capacity}
				</Typography>
			</TicketStatus>
		</StyledTicketCard>
	);
};

export default TicketCard;
